/**
 * Route optimization (M8-B). Resolves a selected stop set to coordinates, runs
 * the vendor-abstracted `RoutingProvider.optimizeRoute` (the OSS-first haversine
 * provider by default — fully client-computable, no external call), and persists
 * the ordered stops + totals in `analysis.routes`. The ~70-stop cap is enforced
 * here with a GRACEFUL refusal (a typed result, never a throw the UI must catch)
 * so the light, non-fleet use case stays inside its lane (M8 R4).
 *
 * The metered-call posture: the caller must pass the M7 entitlement gate before
 * invoking this — a provider call only fires for an entitled, build-go-on tool.
 */

import type { PointSet } from "@/lib/map/pointset";
import {
  MAX_ROUTE_STOPS,
  type OptimizeResult,
  type RouteStop,
  type RoutingProvider,
} from "@/lib/geo/routing";
import { MILES_TO_METERS } from "./geometry";
import type { RouteTotals, Routes } from "./spec";

export { MAX_ROUTE_STOPS } from "@/lib/geo/routing";

/** Outcome of a route-optimization request — success or a graceful refusal. */
export type RouteBuildResult =
  | { ok: true; routes: Routes; result: OptimizeResult; attribution: string }
  | { ok: false; reason: "too_many_stops"; requested: number; limit: number }
  | { ok: false; reason: "too_few_stops"; requested: number };

/** Resolve stop row ids to `{id,coord}` stops (dropping ids not in the set). */
export function resolveStops(pointSet: PointSet, stopRowIds: string[]): RouteStop[] {
  const byId = new Map(pointSet.points.map((p) => [p.id, p]));
  const stops: RouteStop[] = [];
  for (const id of stopRowIds) {
    const p = byId.get(id);
    if (p) stops.push({ id, coord: [p.lng, p.lat] });
  }
  return stops;
}

/**
 * Optimize a route over `stopRowIds`. Refuses gracefully below 2 or above the
 * cap. On success returns the `analysis.routes` block (stops in optimized
 * order) + the raw provider result + the provider attribution.
 */
export async function buildRouteOptimization(
  pointSet: PointSet,
  stopRowIds: string[],
  provider: RoutingProvider,
): Promise<RouteBuildResult> {
  const stops = resolveStops(pointSet, stopRowIds);
  if (stops.length > MAX_ROUTE_STOPS) {
    return { ok: false, reason: "too_many_stops", requested: stops.length, limit: MAX_ROUTE_STOPS };
  }
  if (stops.length < 2) return { ok: false, reason: "too_few_stops", requested: stops.length };

  const result = await provider.optimizeRoute(stops);
  const routes: Routes = { stopRowIds: result.order, optimized: true, totals: result.totals };
  return { ok: true, routes, result, attribution: provider.attribution };
}

/** Human summary of route totals ("38.2 mi · 46 min") — mile/minute first. */
export function routeTotalsLabel(totals: RouteTotals): string {
  const miles = totals.distance / MILES_TO_METERS;
  const minutes = totals.duration / 60;
  const milesLabel = miles >= 10 ? Math.round(miles) : Math.round(miles * 10) / 10;
  return `${milesLabel} mi · ${Math.round(minutes)} min`;
}

/** One ordered export row: the stop's position, id, and a chosen label column. */
export interface RouteExportRow {
  order: number;
  rowId: string;
  label: string;
  lng: number;
  lat: number;
}

/** Ordered stop list for export (CSV/clipboard) — the "exportable ordered list". */
export function routeExportRows(pointSet: PointSet, routes: Routes, labelColumn?: string): RouteExportRow[] {
  const byId = new Map(pointSet.points.map((p) => [p.id, p]));
  const rows: RouteExportRow[] = [];
  routes.stopRowIds.forEach((rowId, i) => {
    const p = byId.get(rowId);
    if (!p) return;
    const label = labelColumn ? String(p.props[labelColumn] ?? "").trim() : "";
    rows.push({ order: i + 1, rowId, label: label !== "" ? label : rowId, lng: p.lng, lat: p.lat });
  });
  return rows;
}

/** Render the ordered list as CSV text (order,label,lng,lat). */
export function routeExportCsv(rows: RouteExportRow[]): string {
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const header = "order,label,lng,lat";
  const body = rows.map((r) => [r.order, esc(r.label), r.lng, r.lat].join(","));
  return [header, ...body].join("\n");
}

/**
 * Recompute a persisted route against a (possibly new) point set — drops stops
 * whose rows no longer exist and re-solves. Used to keep `analysis.routes`
 * consistent after a data reload.
 */
export async function recomputeRoute(
  routes: Routes,
  pointSet: PointSet,
  provider: RoutingProvider,
): Promise<RouteBuildResult> {
  return buildRouteOptimization(pointSet, routes.stopRowIds, provider);
}
